import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Switch } from './ui/switch';
import { Bell, Mail, Briefcase, MessageSquare, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Catalogue of events the user can opt into. Grouped for the UI. The pref
 * keys MUST match `NOTIFICATION_PREF_KEYS` in backend/server.py.
 *
 * adminOnly events are hidden from non-admin users so workers don't see
 * irrelevant toggles.
 */
const SECTIONS = [
  {
    id: 'lifecycle',
    title: 'Job Lifecycle',
    icon: Briefcase,
    description: 'Jobs you watch, are assigned to, or supervise.',
    rows: [
      { key: 'job_started',    label: 'Job started',     hint: 'A teammate clocked in on a job you watch.' },
      { key: 'job_paused',     label: 'Job paused',      hint: 'A job was paused mid-way (with a reason).' },
      { key: 'job_resumed',    label: 'Job resumed',     hint: 'A paused job is back in motion.' },
      { key: 'job_completed',  label: 'Job completed',   hint: 'A job has been finished and signed off.' },
      { key: 'job_assigned',   label: 'Job assigned',    hint: 'A new job lands in your queue.' },
      { key: 'job_reassigned', label: 'Job reassigned',  hint: 'Dispatch moves a job between people or teams.' },
    ],
  },
  {
    id: 'collab',
    title: 'Collaboration',
    icon: MessageSquare,
    description: 'Conversation and document activity.',
    rows: [
      { key: 'job_note',       label: 'New job note',    hint: 'A teammate added a note or replied on a job.' },
      { key: 'card_shared',    label: 'Card shared',     hint: 'A Board card is shared with you.' },
      { key: 'workplan_sent',  label: 'Workplan sent',   hint: 'A workplan is assigned to you.' },
    ],
  },
  {
    id: 'admin',
    title: 'Admin & Alerts',
    icon: ShieldCheck,
    description: 'Operational visibility — usually admin-only.',
    adminOnly: true,
    rows: [
      { key: 'smart_alert',    label: 'Smart alert fired',   hint: 'A KPI / overdue / attendance alert triggered.' },
      { key: 'new_user',       label: 'New user joined',     hint: 'Someone was invited or signed up.' },
      { key: 'qb_error',       label: 'QuickBooks sync error', hint: 'A QB invoice failed to push.' },
      { key: 'weekly_digest',  label: 'Weekly digest',       hint: 'Monday morning company-wide summary email.', emailOnly: true },
    ],
  },
];

export default function NotificationPreferencesDialog({ open, onOpenChange }) {
  const { user, token } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdminLike = user?.role === 'admin' || user?.role === 'supervisor' || user?.is_super_user === true;

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/users/me/notification-prefs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrefs(r.data?.notification_prefs || {});
    } catch (e) {
      toast.error('Failed to load notification preferences');
      setPrefs({});
    }
  }, [token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const setPref = async (key, nextVal) => {
    const prev = prefs?.[key];
    setPrefs((p) => ({ ...p, [key]: nextVal }));
    setSaving(true);
    try {
      await axios.put(
        `${API}/users/me/notification-prefs`,
        { [key]: nextVal },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      // Roll back on error
      setPrefs((p) => ({ ...p, [key]: prev }));
      toast.error('Could not save preference — please retry.');
    } finally {
      setSaving(false);
    }
  };

  const setAllInRow = async (channel, value) => {
    const updates = {};
    SECTIONS.forEach((s) => {
      if (s.adminOnly && !isAdminLike) return;
      s.rows.forEach((r) => {
        if (channel === 'bell' && r.emailOnly) return;
        updates[`${channel}_${r.key}`] = value;
      });
    });
    const before = { ...prefs };
    setPrefs((p) => ({ ...p, ...updates }));
    setSaving(true);
    try {
      await axios.put(`${API}/users/me/notification-prefs`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        `${channel === 'bell' ? 'In-app bell' : 'Email'} ${value ? 'turned on' : 'turned off'} for everything.`,
      );
    } catch (e) {
      setPrefs(before);
      toast.error('Could not save — please retry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#0F1923] border border-[#2D3748] text-white max-h-[85vh] overflow-y-auto"
        data-testid="notification-prefs-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Bell className="w-5 h-5" style={{ color: '#1E88E5' }} />
            Notification Preferences
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: '#94A3B8' }}>
            Pick which events ping the in-app bell and which ones send you an email. The bell badge
            still counts up even when you've muted email — open it any time to catch up.
          </DialogDescription>
        </DialogHeader>

        {/* Quick presets */}
        <div
          className="flex flex-wrap items-center gap-2 mt-2 rounded-xl p-3"
          style={{ background: '#0B1321', border: '1px solid #2D3748' }}
        >
          <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>Quick presets:</span>
          <button
            onClick={() => { setAllInRow('email', true); setAllInRow('bell', true); }}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50"
            style={{ borderColor: '#2D3748', background: '#1A2433', color: '#E2E8F0' }}
            data-testid="preset-email-everything"
          >
            Email + Bell for everything
          </button>
          <button
            onClick={() => { setAllInRow('email', false); setAllInRow('bell', true); }}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50"
            style={{ borderColor: '#1E88E5', background: 'rgba(30,136,229,0.10)', color: '#1E88E5' }}
            data-testid="preset-bell-only"
          >
            Bell only (no email)
          </button>
          <button
            onClick={() => { setAllInRow('email', false); setAllInRow('bell', false); }}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50"
            style={{ borderColor: '#2D3748', background: '#1A2433', color: '#94A3B8' }}
            data-testid="preset-mute-all"
          >
            Go silent (mute all)
          </button>
        </div>

        {/* Matrix */}
        {prefs === null ? (
          <div className="flex items-center gap-2 py-8 text-sm" style={{ color: '#64748B' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your preferences…
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {SECTIONS.map((section) => {
              if (section.adminOnly && !isAdminLike) return null;
              const Icon = section.icon;
              return (
                <section
                  key={section.id}
                  className="rounded-xl"
                  style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                  data-testid={`prefs-section-${section.id}`}
                >
                  <header className="px-4 py-3 flex items-center gap-3 border-b border-[#2D3748]">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(30,136,229,0.10)', color: '#1E88E5' }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-white">{section.title}</h3>
                      <p className="text-xs" style={{ color: '#64748B' }}>{section.description}</p>
                    </div>
                    {/* Column headers */}
                    <div className="hidden sm:flex items-center gap-6 text-[10px] uppercase tracking-wider font-medium" style={{ color: '#64748B' }}>
                      <div className="w-12 text-center flex flex-col items-center">
                        <Bell className="w-3.5 h-3.5 mb-0.5" />
                        Bell
                      </div>
                      <div className="w-12 text-center flex flex-col items-center">
                        <Mail className="w-3.5 h-3.5 mb-0.5" />
                        Email
                      </div>
                    </div>
                  </header>

                  <ul className="divide-y divide-[#1A2433]">
                    {section.rows.map((row) => {
                      const bellKey = `bell_${row.key}`;
                      const emailKey = `email_${row.key}`;
                      const bellVal = prefs[bellKey];
                      const emailVal = prefs[emailKey];
                      return (
                        <li
                          key={row.key}
                          className="px-4 py-3 flex items-center gap-4"
                          data-testid={`prefs-row-${row.key}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{row.label}</p>
                            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{row.hint}</p>
                          </div>
                          <div className="flex items-center gap-6 flex-shrink-0">
                            {/* Bell column — hidden for email-only events */}
                            <div className="w-12 flex justify-center">
                              {row.emailOnly ? (
                                <span className="text-xs" style={{ color: '#475569' }}>—</span>
                              ) : (
                                <Switch
                                  checked={!!bellVal}
                                  onCheckedChange={(v) => setPref(bellKey, v)}
                                  disabled={saving}
                                  data-testid={`pref-bell-${row.key}`}
                                  aria-label={`${row.label} bell`}
                                />
                              )}
                            </div>
                            {/* Email column */}
                            <div className="w-12 flex justify-center">
                              <Switch
                                checked={!!emailVal}
                                onCheckedChange={(v) => setPref(emailKey, v)}
                                disabled={saving}
                                data-testid={`pref-email-${row.key}`}
                                aria-label={`${row.label} email`}
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4">
          <p className="text-[11px] flex-1" style={{ color: '#64748B' }}>
            Changes save automatically. You can always come back here from your profile menu.
          </p>
          <button
            onClick={() => onOpenChange(false)}
            className="text-sm px-4 py-2 rounded-lg font-medium transition-colors"
            style={{ background: '#1E88E5', color: '#fff' }}
            data-testid="notification-prefs-close"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
